import { Locale } from '@/lib/enums/locale.enum';
import { FetchError } from '@/lib/errors/fetchError';

export type FetchOptions = {
  method?: 'GET' | 'PUT' | 'DELETE' | 'POST' | 'PATCH' | 'OPTIONS';
  headers?: Record<string, string>;
  body?: object;
  params?: Record<string, string | string[] | undefined>;
  bearer?: string | null;
  apiKey?: string;
  credentials?: RequestCredentials;
  revalidate?: number;
  timeout?: number;
  hideChannel?: boolean;
  locale?: Locale;
  cache?: RequestCache;
  /** Allow an OK response with no body. Use only for endpoints whose contract is void. */
  allowEmptyBody?: boolean;
  /** Skip parsing the body of an OK response. Non-OK responses are still parsed and thrown. */
  responseType?: 'json' | 'none';
};

const prepareHeaders = (options?: FetchOptions) => {
  const requestHeaders = new Headers();
  requestHeaders.set('accept', 'application/json');
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('User-Agent', process.env.USER_AGENT!);
  if (options?.bearer) {
    requestHeaders.set('Authorization', `Bearer ${options?.bearer}`);
  }
  if (options?.apiKey) {
    requestHeaders.set('api-key', options.apiKey);
  }
  Object.entries(options?.headers || {}).forEach(([key, val]) => {
    requestHeaders.set(key, val);
  });
  return requestHeaders;
};

const prepareUrl = (url: string, options?: FetchOptions) => {
  const requestUrl = new URL(url);
  Object.entries(options?.params || {}).forEach(([key, val]) => {
    if (val) {
      if (Array.isArray(val) && options?.params) {
        ((options.params[key] as string[]) || []).forEach((val: string) => {
          requestUrl.searchParams.append(key, val);
        });
      } else {
        requestUrl.searchParams.append(key, val as string);
      }
    }
  });
  return requestUrl;
};

const prepareRevalidate = (options?: FetchOptions) => {
  return Number.isInteger(options?.revalidate) ? options!.revalidate : 120;
};

type ResponseBody<T> = {
  value: T | string | null;
  isEmpty: boolean;
};

const getBody = async <T>(response: Response, url: string): Promise<ResponseBody<T>> => {
  let rawBody: string;
  try {
    rawBody = await response.text();
  } catch (cause) {
    throw new FetchError(null, url, undefined, { kind: 'network', cause });
  }

  if (rawBody.trim() === '') {
    return { value: null, isEmpty: true };
  }

  try {
    return { value: JSON.parse(rawBody) as T, isEmpty: false };
  } catch (cause) {
    // Error responses are often HTML/plain text. Preserve that body on the HTTP error,
    // while invalid JSON on a successful response is an upstream contract failure.
    if (!response.ok) {
      return { value: rawBody, isEmpty: false };
    }
    throw new FetchError(response.status, url, rawBody, {
      kind: 'invalid-response',
      cause,
    });
  }
};

const defaultTimeout = parseInt(process.env.NEXT_PUBLIC_API_REQUEST_TIMEOUT!, 10);

const getRequestTimeout = (timeout?: number) => {
  const value = timeout ?? defaultTimeout;
  if (!Number.isFinite(value) || value < 0) {
    throw new TypeError('A non-negative API request timeout must be configured');
  }
  return value;
};

export const fetchData = async <T>(url: string, options?: FetchOptions): Promise<T> => {
  const requestHeaders = prepareHeaders(options);
  const requestUrl = prepareUrl(url, options);
  const requestUrlString = requestUrl.toString();
  const timeout = getRequestTimeout(options?.timeout);

  let response: Response;
  try {
    response = await fetch(requestUrl, {
      method: options?.method,
      headers: requestHeaders,
      body: options?.body ? JSON.stringify(options?.body) : undefined,
      credentials: options?.credentials,
      signal: AbortSignal.timeout(timeout),
      next: {
        revalidate: options?.revalidate ? prepareRevalidate(options) : 0,
      },
      cache: options?.cache,
    });
  } catch (e) {
    console.error('request failed', {
      url: requestUrlString,
      e,
    });
    const isTimeout = (e as { name?: string })?.name === 'TimeoutError';
    if (isTimeout) {
      console.error('\n Request exceeded timeout. Aborted! \n');
    }
    throw new FetchError(null, requestUrlString, undefined, {
      kind: isTimeout ? 'timeout' : 'network',
      cause: e,
    });
  }

  if (response.ok && options?.responseType === 'none') {
    return undefined as T;
  }

  const result = await getBody<T>(response, requestUrlString);
  if (!response.ok) {
    console.error('request failed', {
      url: requestUrlString,
      responseOk: response.ok,
      responseStatus: response.status,
      responseHeaders: Object.fromEntries(response.headers),
      responseBody: result.value,
    });
    throw new FetchError(response.status, requestUrlString, result.value);
  }

  if (result.isEmpty && !options?.allowEmptyBody) {
    throw new FetchError(response.status, requestUrlString, undefined, {
      kind: 'invalid-response',
    });
  }

  return result.value as T;
};
